import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import {
  completeCaseAction,
  getCaseSettings,
  runCaseCommand,
  type HrCase,
  type HrCaseAction,
} from '@/api/services/hrCases.service';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AcknowledgeForm,
  AppealDecisionForm,
  FindingsForm,
  type CaseAction,
} from './CaseActionForms';
import {
  APPEAL_LABELS,
  FINDING_LABELS,
  formatCaseDate,
  ROUTING_REASONS,
  TERMINAL_STATUSES,
} from './caseFormat';
import { useCaseCommand } from './useCaseCommand';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

/** Who raised the grievance, whom it names, its deadlines, and how it was routed. */
export function GrievanceSummary({ hrCase }: { hrCase: HrCase }): React.ReactElement {
  const routing = hrCase.routing;
  return (
    <div className="space-y-4">
      {hrCase.status === 'blocked' && (
        <p
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
          role="note"
        >
          No handler could be assigned without involving someone the grievance concerns. Add a
          handler who is not involved to route it.
        </p>
      )}
      {routing?.route === 'alternate' && hrCase.status !== 'blocked' && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Routed to the alternate handlers because{' '}
          {ROUTING_REASONS[routing.reason ?? ''] ?? 'the usual handlers could not take it'}.
        </p>
      )}
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
        <Field label="Raised by">
          {hrCase.complainant?.name ?? '—'}
          {hrCase.complainant?.employeeId && (
            <span className="block text-xs text-gray-600 dark:text-gray-300">
              {hrCase.complainant.employeeId}
            </span>
          )}
        </Field>
        <Field label="About">{hrCase.subject?.name ?? 'No one named'}</Field>
        <Field label="Raised">{formatCaseDate(hrCase.openedAt)}</Field>
        {hrCase.status === 'submitted' && (
          <Field label="Acknowledge by">{formatCaseDate(hrCase.deadlines.acknowledgeBy)}</Field>
        )}
        {hrCase.outcome?.finding && (
          <Field label="Finding">{FINDING_LABELS[hrCase.outcome.finding]}</Field>
        )}
        {hrCase.deadlines.appealClosesAt && !hrCase.appeal && (
          <Field label="Appeal window closes">
            {formatCaseDate(hrCase.deadlines.appealClosesAt)}
          </Field>
        )}
        {hrCase.appeal && (
          <Field label="Appeal">
            {hrCase.appeal.decision ? APPEAL_LABELS[hrCase.appeal.decision] : 'Awaiting decision'}
            <span className="block text-xs text-gray-600 dark:text-gray-300">
              Requested {formatCaseDate(hrCase.appeal.requestedAt)}
            </span>
          </Field>
        )}
        {hrCase.withdrawnReason && (
          <div className="sm:col-span-3">
            <Field label="Withdrawn">{hrCase.withdrawnReason}</Field>
          </div>
        )}
      </dl>
    </div>
  );
}

function CompleteActionButton({
  companyId,
  hrCase,
  action,
}: {
  companyId: string;
  hrCase: HrCase;
  action: HrCaseAction;
}): React.ReactElement {
  const run = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId) => completeCaseAction(companyId, hrCase._id, action.index, { requestId }),
    'Action marked complete',
    () => undefined
  );
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => run.submit(undefined)}
      loading={run.isPending}
    >
      Mark complete <span className="sr-only">{action.description}</span>
    </Button>
  );
}

/** The follow-up actions recorded with the findings. */
export function GrievanceActionList({
  companyId,
  hrCase,
  canAct,
}: {
  companyId: string;
  hrCase: HrCase;
  canAct: boolean;
}): React.ReactElement | null {
  const actions = hrCase.outcome?.actions ?? [];
  if (actions.length === 0) return null;
  const open = !TERMINAL_STATUSES.includes(hrCase.status);
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Actions</h3>
      <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {actions.map((action) => (
          <li
            key={action.index}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
          >
            <span className="min-w-0">
              <span className="block break-words text-sm font-medium text-gray-900 dark:text-gray-100">
                {action.description}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {[
                  action.owner && `Owner: ${action.owner.name}`,
                  action.dueAt && `Due ${formatCaseDate(action.dueAt)}`,
                  action.linkedCase && `Disciplinary case ${action.linkedCase.reference}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {action.completedAt ? (
              <Badge variant="success" size="sm">
                <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
                Done {formatCaseDate(action.completedAt)}
              </Badge>
            ) : (
              canAct &&
              open && <CompleteActionButton companyId={companyId} hrCase={hrCase} action={action} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function InvestigateButton({ companyId, hrCase }: { companyId: string; hrCase: HrCase }) {
  const run = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId) =>
      runCaseCommand(companyId, hrCase._id, { command: 'investigate', input: { requestId } }),
    'Investigation started',
    () => undefined
  );
  return (
    <Button size="sm" onClick={() => run.submit(undefined)} loading={run.isPending}>
      Start investigation
    </Button>
  );
}

function CloseButton({ companyId, hrCase }: { companyId: string; hrCase: HrCase }) {
  const run = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId) =>
      runCaseCommand(companyId, hrCase._id, { command: 'close', input: { requestId } }),
    'Grievance closed',
    () => undefined
  );
  return (
    <Button size="sm" onClick={() => run.submit(undefined)} loading={run.isPending}>
      Close grievance
    </Button>
  );
}

/** Grievance commands allowed by status and the viewer's role on the team. */
export function GrievanceActions({
  companyId,
  hrCase,
}: {
  companyId: string;
  hrCase: HrCase;
}): React.ReactElement {
  const [action, setAction] = useState<CaseAction | null>(null);
  const { can } = usePermissions();
  const settings = useQuery({
    queryKey: ['hr-case-settings', companyId],
    queryFn: () => getCaseSettings(companyId),
    enabled: action === 'findings',
  });
  const isHandler = hrCase.viewerRoles.includes('handler');
  const isOfficer = hrCase.viewerRoles.includes('appeal_officer');
  const hasOfficer = hrCase.members.some(
    (member) => !member.removedAt && member.role === 'appeal_officer'
  );
  const closable = ['findings_issued', 'appeal_decided'].includes(hrCase.status);
  const done = (): void => setAction(null);
  const handlerOnly = ['submitted', 'investigating'].includes(hrCase.status) && !isHandler;

  return (
    <section aria-label="Case actions" className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {hrCase.status === 'submitted' && isHandler && (
          <Button size="sm" onClick={() => setAction('acknowledge')}>
            Acknowledge
          </Button>
        )}
        {hrCase.status === 'acknowledged' && (
          <InvestigateButton companyId={companyId} hrCase={hrCase} />
        )}
        {hrCase.status === 'investigating' && isHandler && (
          <Button size="sm" onClick={() => setAction('findings')}>
            Issue findings
          </Button>
        )}
        {hrCase.status === 'appeal_requested' && isOfficer && (
          <Button size="sm" onClick={() => setAction('appeal')}>
            Decide appeal
          </Button>
        )}
        {closable && !hrCase.appealOpen && <CloseButton companyId={companyId} hrCase={hrCase} />}
      </div>
      {handlerOnly && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Only a handler can{' '}
          {hrCase.status === 'submitted' ? 'acknowledge the grievance' : 'issue the findings'}.
        </p>
      )}
      {hrCase.appealOpen && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          The employee can appeal until {formatCaseDate(hrCase.deadlines.appealClosesAt)}; the
          grievance can close after that.
        </p>
      )}
      {hrCase.status === 'appeal_requested' && !isOfficer && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          {hasOfficer
            ? 'Only the case appeal officer can decide the appeal.'
            : 'Add an appeal officer who is not a handler or investigator.'}
        </p>
      )}
      {action === 'acknowledge' && (
        <AcknowledgeForm companyId={companyId} hrCase={hrCase} onDone={done} />
      )}
      {action === 'findings' && (
        <FindingsForm
          companyId={companyId}
          hrCase={hrCase}
          onDone={done}
          canOpenDiscipline={can('hr', 'discipline')}
          disciplineTypes={(settings.data?.disciplineCaseTypes ?? []).filter((type) => type.active)}
        />
      )}
      {action === 'appeal' && (
        <AppealDecisionForm companyId={companyId} hrCase={hrCase} onDone={done} />
      )}
    </section>
  );
}
